import { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Upload, Calendar, MapPin, User, Phone, Hash, FileText, Clock, X } from 'lucide-react';
import { useContent } from '@/hooks/useContent';
import { createHomeVisitRequest } from '@/services/homeVisitApi';

interface FormData {
  name: string;
  code: string;
  phone1: string;
  phone2: string;
  age: string;
  tests: string;
  prescription: File | null;
  preferredDate: string;
  preferredTime: string;
  address: string;
}

const HomeVisitRequest = () => {
  const { t } = useContent();
  const sectionRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    code: '',
    phone1: '',
    phone2: '',
    age: '',
    tests: '',
    prescription: null,
    preferredDate: '',
    preferredTime: '',
    address: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    document.title = t('homeVisitRequest.title') || 'Home Visit Request - ElhamyLabs';
  }, [t]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('homeVisitRequest.errors.nameRequired') || 'Name is required';
    }

    if (!formData.phone1.trim()) {
      newErrors.phone1 = t('homeVisitRequest.errors.phoneRequired') || 'Phone number is required';
    } else if (!/^[0-9+\-\s()]+$/.test(formData.phone1)) {
      newErrors.phone1 = t('homeVisitRequest.errors.phoneInvalid') || 'Invalid phone number format';
    }

    if (formData.phone2 && !/^[0-9+\-\s()]+$/.test(formData.phone2)) {
      newErrors.phone2 = t('homeVisitRequest.errors.phoneInvalid') || 'Invalid phone number format';
    }

    if (!formData.age) {
      newErrors.age = t('homeVisitRequest.errors.ageRequired') || 'Age is required';
    }

    if (!formData.address.trim()) {
      newErrors.address = t('homeVisitRequest.errors.addressRequired') || 'Address is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string | File | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    handleInputChange('prescription', file);
  };

  const handleRemoveFile = () => {
    handleInputChange('prescription', null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      // Combine date and time into ISO 8601 format
      let requestedDate: string;
      if (formData.preferredDate && formData.preferredTime) {
        const dateTime = new Date(`${formData.preferredDate}T${formData.preferredTime}`);
        requestedDate = dateTime.toISOString();
      } else if (formData.preferredDate) {
        const dateTime = new Date(`${formData.preferredDate}T12:00:00`);
        requestedDate = dateTime.toISOString();
      } else {
        // Default to current date/time if not provided
        requestedDate = new Date().toISOString();
      }

      // Build notes field - include age and tests
      let notes = '';
      if (formData.age) {
        notes += `Age: ${formData.age}`;
      }
      if (formData.tests) {
        if (notes) notes += '\n\n';
        notes += `Required Tests:\n${formData.tests}`;
      }

      // Call the API
      await createHomeVisitRequest({
        patientName: formData.name,
        patientCode: formData.code || undefined,
        primaryPhone: formData.phone1,
        secondaryPhone: formData.phone2 || undefined,
        address: formData.address,
        requestedDate: requestedDate,
        branchId: 3, // Default branch ID
        notes: notes || undefined,
        prescriptionImage: formData.prescription || undefined,
      });

      // Success - reset form
      setSubmitSuccess(true);
      setFormData({
        name: '',
        code: '',
        phone1: '',
        phone2: '',
        age: '',
        tests: '',
        prescription: null,
        preferredDate: '',
        preferredTime: '',
        address: '',
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError(
        error instanceof Error
          ? error.message
          : t('homeVisitRequest.errors.submitFailed') || 'Failed to submit request. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generate age options (1-120)
  const ageOptions = Array.from({ length: 120 }, (_, i) => i + 1);

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-dark-primary dark:via-dark-secondary dark:to-dark-primary">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-brand-orange-base via-brand-orange-light to-brand-orange-base dark:from-brand-purple-dark dark:via-brand-purple-base">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4 text-white">
            {t('homeVisitRequest.title') || 'طلب زيارة منزلية'}
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            {t('homeVisitRequest.subtitle') || 'املأ النموذج أدناه لطلب زيارة منزلية من فريقنا الطبي'}
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section ref={sectionRef} className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-white dark:bg-dark-secondary rounded-2xl shadow-2xl p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Field */}
                <div>
                  <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-light-text dark:text-dark-text">
                    <User size={18} className="text-brand-orange-base dark:text-brand-purple-base" />
                    {t('homeVisitRequest.form.name') || 'الاسم'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder={t('homeVisitRequest.form.namePlaceholder') || 'أدخل اسمك الكامل'}
                    required
                    className={`w-full px-4 py-3 rounded-lg bg-light-primary dark:bg-dark-primary text-light-text dark:text-dark-text border-2 transition-all ${
                      errors.name
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-light-text/10 dark:border-dark-text/10 focus:border-brand-orange-base dark:focus:border-brand-purple-base'
                    } focus:outline-none focus:ring-2 focus:ring-brand-orange-base/20 dark:focus:ring-brand-purple-base/20`}
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
                </div>

                {/* Code Field */}
                <div>
                  <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-light-text dark:text-dark-text">
                    <Hash size={18} className="text-brand-orange-base dark:text-brand-purple-base" />
                    {t('homeVisitRequest.form.code') || 'الكود (إن وجد)'}
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => handleInputChange('code', e.target.value)}
                    placeholder={t('homeVisitRequest.form.codePlaceholder') || 'أدخل الكود إن وجد'}
                    className="w-full px-4 py-3 rounded-lg bg-light-primary dark:bg-dark-primary text-light-text dark:text-dark-text border-2 border-light-text/10 dark:border-dark-text/10 focus:outline-none focus:ring-2 focus:ring-brand-orange-base/20 dark:focus:ring-brand-purple-base/20 focus:border-brand-orange-base dark:focus:border-brand-purple-base transition-all"
                  />
                </div>

                {/* Phone 1 Field */}
                <div>
                  <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-light-text dark:text-dark-text">
                    <Phone size={18} className="text-brand-orange-base dark:text-brand-purple-base" />
                    {t('homeVisitRequest.form.phone1') || 'رقم الهاتف 1'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.phone1}
                    onChange={(e) => handleInputChange('phone1', e.target.value)}
                    placeholder={t('homeVisitRequest.form.phonePlaceholder') || 'أدخل رقم الهاتف'}
                    required
                    className={`w-full px-4 py-3 rounded-lg bg-light-primary dark:bg-dark-primary text-light-text dark:text-dark-text border-2 transition-all ${
                      errors.phone1
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-light-text/10 dark:border-dark-text/10 focus:border-brand-orange-base dark:focus:border-brand-purple-base'
                    } focus:outline-none focus:ring-2 focus:ring-brand-orange-base/20 dark:focus:ring-brand-purple-base/20`}
                  />
                  {errors.phone1 && <p className="mt-1 text-sm text-red-500">{errors.phone1}</p>}
                </div>

                {/* Phone 2 Field */}
                <div>
                  <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-light-text dark:text-dark-text">
                    <Phone size={18} className="text-brand-orange-base dark:text-brand-purple-base" />
                    {t('homeVisitRequest.form.phone2') || 'رقم الهاتف 2 (إن وجد)'}
                  </label>
                  <input
                    type="tel"
                    value={formData.phone2}
                    onChange={(e) => handleInputChange('phone2', e.target.value)}
                    placeholder={t('homeVisitRequest.form.phonePlaceholder') || 'أدخل رقم الهاتف الثاني'}
                    className={`w-full px-4 py-3 rounded-lg bg-light-primary dark:bg-dark-primary text-light-text dark:text-dark-text border-2 transition-all ${
                      errors.phone2
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-light-text/10 dark:border-dark-text/10 focus:border-brand-orange-base dark:focus:border-brand-purple-base'
                    } focus:outline-none focus:ring-2 focus:ring-brand-orange-base/20 dark:focus:ring-brand-purple-base/20`}
                  />
                  {errors.phone2 && <p className="mt-1 text-sm text-red-500">{errors.phone2}</p>}
                </div>

                {/* Age Field */}
                <div>
                  <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-light-text dark:text-dark-text">
                    <User size={18} className="text-brand-orange-base dark:text-brand-purple-base" />
                    {t('homeVisitRequest.form.age') || 'العمر'} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.age}
                    onChange={(e) => handleInputChange('age', e.target.value)}
                    required
                    className={`w-full px-4 py-3 rounded-lg bg-light-primary dark:bg-dark-primary text-light-text dark:text-dark-text border-2 transition-all appearance-none cursor-pointer ${
                      errors.age
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-light-text/10 dark:border-dark-text/10 focus:border-brand-orange-base dark:focus:border-brand-purple-base'
                    } focus:outline-none focus:ring-2 focus:ring-brand-orange-base/20 dark:focus:ring-brand-purple-base/20`}
                  >
                    <option value="">{t('homeVisitRequest.form.agePlaceholder') || 'اختر العمر'}</option>
                    {ageOptions.map((age) => (
                      <option key={age} value={age}>
                        {age}
                      </option>
                    ))}
                  </select>
                  {errors.age && <p className="mt-1 text-sm text-red-500">{errors.age}</p>}
                </div>

                {/* Tests Text Field */}
                <div>
                  <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-light-text dark:text-dark-text">
                    <FileText size={18} className="text-brand-orange-base dark:text-brand-purple-base" />
                    {t('homeVisitRequest.form.tests') || 'اكتب التحاليل المطلوبة'}
                  </label>
                  <textarea
                    value={formData.tests}
                    onChange={(e) => handleInputChange('tests', e.target.value)}
                    placeholder={t('homeVisitRequest.form.testsPlaceholder') || 'اكتب قائمة بالتحاليل المطلوبة'}
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg bg-light-primary dark:bg-dark-primary text-light-text dark:text-dark-text border-2 border-light-text/10 dark:border-dark-text/10 focus:outline-none focus:ring-2 focus:ring-brand-orange-base/20 dark:focus:ring-brand-purple-base/20 focus:border-brand-orange-base dark:focus:border-brand-purple-base transition-all resize-none"
                  />
                </div>

                {/* Prescription Upload */}
                <div>
                  <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-light-text dark:text-dark-text">
                    <Upload size={18} className="text-brand-orange-base dark:text-brand-purple-base" />
                    {t('homeVisitRequest.form.prescription') || 'رفع صورة الروشته'}
                  </label>
                  <div className="space-y-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                      id="prescription-upload"
                    />
                    <label
                      htmlFor="prescription-upload"
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg bg-light-primary dark:bg-dark-primary text-light-text dark:text-dark-text border-2 border-dashed border-light-text/20 dark:border-dark-text/20 hover:border-brand-orange-base dark:hover:border-brand-purple-base cursor-pointer transition-all"
                    >
                      <Upload size={20} />
                      <span>{t('homeVisitRequest.form.uploadPrescription') || 'انقر لرفع صورة الروشته'}</span>
                    </label>
                    {formData.prescription && (
                      <div className="flex items-center justify-between p-3 bg-light-primary dark:bg-dark-primary rounded-lg border border-light-text/10 dark:border-dark-text/10">
                        <span className="text-sm text-light-text dark:text-dark-text truncate flex-1">
                          {formData.prescription.name}
                        </span>
                        <button
                          type="button"
                          onClick={handleRemoveFile}
                          className="ml-2 p-1 hover:bg-red-500/10 rounded transition-colors"
                        >
                          <X size={18} className="text-red-500" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Date and Time */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-light-text dark:text-dark-text">
                      <Calendar size={18} className="text-brand-orange-base dark:text-brand-purple-base" />
                      {t('homeVisitRequest.form.preferredDate') || 'التاريخ المفضل'}
                    </label>
                    <input
                      type="date"
                      value={formData.preferredDate}
                      onChange={(e) => handleInputChange('preferredDate', e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 rounded-lg bg-light-primary dark:bg-dark-primary text-light-text dark:text-dark-text border-2 border-light-text/10 dark:border-dark-text/10 focus:outline-none focus:ring-2 focus:ring-brand-orange-base/20 dark:focus:ring-brand-purple-base/20 focus:border-brand-orange-base dark:focus:border-brand-purple-base transition-all"
                    />
                  </div>
                  <div>
                    <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-light-text dark:text-dark-text">
                      <Clock size={18} className="text-brand-orange-base dark:text-brand-purple-base" />
                      {t('homeVisitRequest.form.preferredTime') || 'الوقت المفضل'}
                    </label>
                    <input
                      type="time"
                      value={formData.preferredTime}
                      onChange={(e) => handleInputChange('preferredTime', e.target.value)}
                      className="w-full px-4 py-3 rounded-lg bg-light-primary dark:bg-dark-primary text-light-text dark:text-dark-text border-2 border-light-text/10 dark:border-dark-text/10 focus:outline-none focus:ring-2 focus:ring-brand-orange-base/20 dark:focus:ring-brand-purple-base/20 focus:border-brand-orange-base dark:focus:border-brand-purple-base transition-all"
                    />
                  </div>
                </div>

                {/* Address Field */}
                <div>
                  <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-light-text dark:text-dark-text">
                    <MapPin size={18} className="text-brand-orange-base dark:text-brand-purple-base" />
                    {t('homeVisitRequest.form.address') || 'العنوان بالتفصيل'} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder={t('homeVisitRequest.form.addressPlaceholder') || 'أدخل العنوان بالتفصيل'}
                    required
                    rows={4}
                    className={`w-full px-4 py-3 rounded-lg bg-light-primary dark:bg-dark-primary text-light-text dark:text-dark-text border-2 transition-all ${
                      errors.address
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-light-text/10 dark:border-dark-text/10 focus:border-brand-orange-base dark:focus:border-brand-purple-base'
                    } focus:outline-none focus:ring-2 focus:ring-brand-orange-base/20 dark:focus:ring-brand-purple-base/20 resize-none`}
                  />
                  {errors.address && <p className="mt-1 text-sm text-red-500">{errors.address}</p>}
                </div>

                {/* Error Message */}
                {submitError && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-600 dark:text-red-400">{submitError}</p>
                  </div>
                )}

                {/* Success Message */}
                {submitSuccess && (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <p className="text-sm text-green-600 dark:text-green-400">
                      {t('homeVisitRequest.successMessage') || 'Request submitted successfully!'}
                    </p>
                  </div>
                )}

                {/* Submit Button */}
                <div className="pt-4">
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-gradient-to-r from-brand-orange-base to-brand-orange-light text-white rounded-lg font-semibold hover:from-brand-orange-light hover:to-brand-orange-base transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                    whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {t('homeVisitRequest.form.submitting') || 'جاري الإرسال...'}
                      </>
                    ) : (
                      <>
                        {t('homeVisitRequest.form.submit') || 'إرسال الطلب'}
                      </>
                    )}
                  </motion.button>
                </div>
              </form>
            </div>
        </div>
      </section>
    </div>
  );
};

export default HomeVisitRequest;

